import { useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'
import { MusePeer } from '@/services/RoomContextMusePeer'
import { useRoomContext } from '@/RoomContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSideBarWidthObserver from './useSideBarWidthObserver'
import { debounce } from 'lodash'
import { videoSubscriptionManager } from '@/services/VideoSubscriptionManager'
import { logger as rootLogger } from '@/services/Logger'
import { collapsibleGroupManager } from '@/UIComponents2/useCollapsibleGroup'

const log = rootLogger.createChildLogger({ name: 'SidebarVirtualization' })

class HeightCache {
    private cache: Map<string, number> = new Map()

    private totalHeightChangedDebouceTimeout: NodeJS.Timeout | undefined = undefined
    onTotalHeightChanged: (() => void) | null = null

    set(sessionId: string, height: number) {
        if (!this.cache.has(sessionId)) {
            this.cache.set(sessionId, 0)
        }

        const h = this.cache.get(sessionId)
        let hPrev = h
        this.cache.set(sessionId, height)

        log.verbose('HeightCache', `(sessionId=${sessionId}) store height=${height})`)

        // used to trigger the recalculation of deriveNormalPeerVisibilityMap() when height
        // of a card changes, with a reasonable debounce.
        if (height !== hPrev) {
            clearTimeout(this.totalHeightChangedDebouceTimeout)
            this.totalHeightChangedDebouceTimeout = setTimeout(() => {
                this.onTotalHeightChanged && this.onTotalHeightChanged()
            }, 200)
        }
    }

    get(sessionId: string) {
        const height = this.cache.get(sessionId) || 300
        log.verbose('HeightCache', `(sessionId=${sessionId}) get height=${height})`)
        return height
    }
}
export const sidebarVirtualizationHeightCache = new HeightCache()

// the y-margin of SideBarRemotePeerListItem
const scrollCardMarginY = 7

class ScrollContainerHeightCache {
    private height: number = 800
    get() {
        return this.height
    }
    set(height: number) {
        this.height = height
    }
}
const scrollContainerHeightCache = new ScrollContainerHeightCache()

export function useSidebarVirtualizationService() {
    const viewMode = useViewMode()
    const { divRef } = useSideBarWidthObserver()
    const [roomState] = useRoomContext()
    const abovePeersContainerRef = useRef<HTMLDivElement>(null)
    const [abovePeersContainerHeight, setAbovePeersContainerHeight] = useState<number>(0)
    const [scrollContainerHeight, setScrollContainerHeight] = useState<number>(scrollContainerHeightCache.get())
    const [scrollPos, setScrollPos] = useState<number>(0)
    const [accordionStateChangedTrigger, setAccordionStateChangedTrigger] = useState(0)

    useEffect(() => {
        const handleResize = debounce(() => {
            const h = document.documentElement.clientHeight

            // an estimation: the scroll height cannot be greater than this.
            if (scrollContainerHeightCache.get() !== h) {
                setScrollContainerHeight(h)
            }
            scrollContainerHeightCache.set(h)
            log.verbose(`scrollContainerHeightCache`, `storing scrollContainerHeight ${h}.`)
        }, 100)

        const resizeObserver = new ResizeObserver(handleResize)

        resizeObserver.observe(document.documentElement)

        return () => {
            handleResize.cancel()
            resizeObserver.disconnect()
        }
    }, [])

    const onScroll = useCallback(
        debounce(() => {
            if (divRef.current) {
                const pos = divRef.current.scrollTop
                setScrollPos(pos)
                log.verbose(`scrollPos`, `storing scrollPos=${pos}.`)
            }
        }, 100),
        []
    )

    let tRef = useRef<NodeJS.Timer | undefined>(undefined)
    useEffect(() => {
        tRef.current = setTimeout(() => {
            if (abovePeersContainerRef.current) {
                setAbovePeersContainerHeight(abovePeersContainerRef.current.clientHeight)
                log.verbose(
                    `abovePeersContainerHeight`,
                    `storing abovePeersContainerHeight=${abovePeersContainerRef.current.clientHeight}.`
                )

                // @ts-ignore
                document.abovePeersContainer = abovePeersContainerRef.current
            }
        }, 500)

        return () => {
            clearTimeout(tRef.current)
        }
    }, [abovePeersContainerRef.current, roomState.roomWaitlist, viewMode.galleryHidden])

    const deriveNormalPeerVisibilityMap = useCallback(
        (normalPeers: MusePeer[]) => {
            const map = {} as Record<string, boolean>

            log.verbose(`deriveNormalPeerVisibilityMap`, `called.`, {
                scrollPos,
                scrollContainerHeight,
                abovePeersContainerHeight,
                galleryHidden: !!viewMode.galleryHidden,
            })

            const trace: { sessionId: string; username: string; height: number; currHeight: number; b: boolean }[] = []

            let currHeight = abovePeersContainerHeight
            normalPeers.forEach((peer) => {
                const height = sidebarVirtualizationHeightCache.get(peer.sessionId)

                currHeight += Math.round(height) + scrollCardMarginY

                const margin = 800
                if (currHeight < scrollPos - margin) {
                    map[peer.sessionId] = false
                    trace.push({ sessionId: peer.sessionId, username: peer.username, height, currHeight, b: false })
                } else if (currHeight - height > scrollPos + scrollContainerHeight) {
                    map[peer.sessionId] = false
                    trace.push({ sessionId: peer.sessionId, username: peer.username, height, currHeight, b: false })
                } else {
                    map[peer.sessionId] = true
                    trace.push({ sessionId: peer.sessionId, username: peer.username, height, currHeight, b: true })
                }

                // set video visibility states for videoSubscriptionManager. If the video gallery
                // is active, then we don't need to set the visibility states because the
                // VideoGalleryPanel will handle it. (viewMode.galleryHidden == gallery is not active)
                if (viewMode.galleryHidden) {
                    if (peer.manifest?.localSfuVideo) {
                        if (map[peer.sessionId]) {
                            videoSubscriptionManager.setVisible(peer.sessionId)
                        } else {
                            videoSubscriptionManager.setInvisible(peer.sessionId)
                        }
                    } else {
                        videoSubscriptionManager.setNoVideo(peer.sessionId)
                    }
                }
            })

            log.verbose(`deriveNormalPeerVisibilityMap`, `done.`, { trace })

            return map
        },
        [
            scrollPos,
            scrollContainerHeight,
            viewMode.galleryHidden,
            viewMode.sidebarVideosHidden,
            abovePeersContainerHeight,
            accordionStateChangedTrigger,
        ]
    )

    useEffect(() => {
        const h = (_open: boolean, groupId: string) => {
            if (groupId === 'sidebar-user-controls') {
                // triggers the recalculation of deriveNormalPeerVisibilityMap()
                setAccordionStateChangedTrigger((n) => n + 1)
            }
        }

        // used to trigger the recalculation of deriveNormalPeerVisibilityMap() when height
        // of a card changes
        sidebarVirtualizationHeightCache.onTotalHeightChanged = () => {
            setAccordionStateChangedTrigger((n) => n + 1)
        }

        collapsibleGroupManager.on('change', h)

        return () => {
            collapsibleGroupManager.off('change', h)
        }
    }, [])

    return {
        divRef,
        onScroll,
        deriveNormalPeerVisibilityMap,
    }
}
